import type { Competitor, CompetitorBrief, BriefScope } from '@ispot/shared'
import { CompetitorBriefRow } from './CompetitorBriefRow'

interface CompetitorCategorySpreadProps {
  category: string
  competitors: Competitor[]
  expandedCompetitorId: string | null
  activeBrief: CompetitorBrief | null
  isBriefLoading: boolean
  onOpenCompetitor: (id: string) => void
  onCloseBrief: () => void
  onGenerateBrief: (scope: BriefScope, apiKey: string) => Promise<void>
  onEnrich?: (id: string, apiKey: string) => Promise<void>
}

type DominantType = 'direct' | 'adjacent' | 'potential'

function dominantType(competitors: Competitor[]): DominantType {
  if (competitors.some(c => c.type === 'direct')) return 'direct'
  if (competitors.some(c => c.type === 'adjacent')) return 'adjacent'
  return 'potential'
}

const ACCENT: Record<DominantType, string> = {
  direct: 'var(--color-accent)',
  adjacent: 'var(--color-market)',
  potential: 'var(--color-border-strong)',
}
const EYEBROW_COLOR: Record<DominantType, string> = {
  direct: 'var(--color-accent)',
  adjacent: 'var(--color-market-dark)',
  potential: 'var(--color-text-secondary)',
}
const EYEBROW_LABEL: Record<DominantType, string> = {
  direct: 'Direct competition',
  adjacent: 'Adjacent',
  potential: 'Potential',
}

export function CompetitorCategorySpread({
  category,
  competitors,
  expandedCompetitorId,
  activeBrief,
  isBriefLoading,
  onOpenCompetitor,
  onCloseBrief,
  onGenerateBrief,
  onEnrich,
}: CompetitorCategorySpreadProps) {
  const dominant = dominantType(competitors)
  const direct = competitors.filter(c => c.type === 'direct').length
  const adjacent = competitors.filter(c => c.type === 'adjacent').length
  const potential = competitors.filter(c => c.type === 'potential').length
  const parts: string[] = []
  if (direct > 0) parts.push(`${direct} direct`)
  if (adjacent > 0) parts.push(`${adjacent} adjacent`)
  if (potential > 0) parts.push(`${potential} potential`)

  return (
    <article
      style={{
        display: 'grid',
        gridTemplateColumns: '280px 1fr',
        columnGap: '56px',
        alignItems: 'start',
      }}
    >
      <header style={{ position: 'sticky', top: 24, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: EYEBROW_COLOR[dominant],
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <span aria-hidden="true" style={{ width: '20px', height: '1px', background: ACCENT[dominant] }} />
          {EYEBROW_LABEL[dominant]}
        </span>
        <h3
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: '24px',
            letterSpacing: '-0.018em',
            lineHeight: 1.15,
            margin: '10px 0 0',
            color: 'var(--color-text)',
          }}
        >
          {category}
        </h3>
        <div
          style={{
            marginTop: '10px',
            fontFamily: 'var(--font-mono)',
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            letterSpacing: '0.02em',
          }}
        >
          <strong style={{ color: 'var(--color-text)', fontWeight: 500 }}>{competitors.length}</strong>
          {parts.length > 0 && <span> · {parts.join(', ')}</span>}
        </div>
      </header>

      <div>
        {competitors.map((c, i) => {
          const isExpanded = expandedCompetitorId === c.id
          return (
            <CompetitorBriefRow
              key={c.id}
              competitor={c}
              isExpanded={isExpanded}
              brief={isExpanded ? activeBrief : null}
              isLoading={isExpanded && isBriefLoading}
              isFirst={i === 0}
              onOpen={onOpenCompetitor}
              onClose={onCloseBrief}
              onGenerate={onGenerateBrief}
              onEnrich={onEnrich}
            />
          )
        })}
      </div>
    </article>
  )
}
